// All user-visible strings, keyed by locale.

const translations = {
  en: {
    // NavBar
    nav: {
      brand: 'WellnessCraft',
      home: 'Home',
      explorer: 'Recipe Explorer',
      submit: 'Submit a Recipe',
      favorites: 'Favorites',
      tips: 'Tips',
      search: 'Search recipes...',
      notifications: 'Notifications',
      account: 'Account',
      logout: 'Logout',
      switchLang: 'He',
      articles: 'Articles',
    },

    // Hero
    hero: {
      headingLead: 'Gourmet Recipes',
      headingTrail: 'to Your Health & Lifestyle',
      highlight: 'Tailored',
      description:
        'Elevate your wellness journey with expert-led nutrition. Discover healthy ingredient swaps and curated recipe collections for sugar-free, pregnancy-safe, or high-protein rituals.',
      primaryAction: { label: 'Start Your Journey', href: '/explorer' },
      generateAction: 'Generate from Ingredients',
      weeklyPickLabel: 'Weekly Pick',
      weeklyPick: 'Organic Swap • Trade refined sugar for date nectar',
      imageAlt: 'Vibrant salad bowl with seasonal produce',
    },

    // Gourmet Section
    gourmet: {
      heading: "Today's Gourmet Selection",
      description:
        'Elevated dishes curated for specific wellness goals. Tap into high-protein, sugar-free, or weight balance plates.',
      prevLabel: 'Previous selection',
      nextLabel: 'Next selection',
      saveLabel: 'Save recipe',
    },

    // Newsletter / Consultation
    newsletter: {
      heading: 'Book an Initial 1:1 Consultation',
      body2:
        'I invite you to reach out for an initial consultation so we can connect, discuss your specific needs, and explore how clinical nutrition, coaching tools, and evidence-based psychological strategies can support your journey.',
      placeholder: 'yourname@example.com',
      emailLabel: 'Your email address',
      sendButton: 'Send Request',
      sendingButton: 'Sending…',
      defaultMeta:
        '',
    },

    // Explorer Page
    explorer: {
      eyebrow: 'Curated Collection',
      title: 'Recipes Explorer',
      accent: '& Archives',
      description:
        'A curated collection of global gastronomy. Discover heritage recipes reimagined for the contemporary kitchen.',
      filterOpen: 'Open filters',
      recipeMetaFallback: 'Editorial Nourishment',
      addRecipe: 'Add a new recipe',
      generateEntryPoint: 'Generate a recipe from ingredients',
      searchPlaceholder: 'Search recipes…',
      searchScopeTitle: 'Title only',
      searchScopeContent: 'Full content',
      searchNoResults: 'No recipes match your search.',
      modeSearch: 'Search Existing',
      modeGenerate: 'Generate From Ingredients',
      generateTitle: 'Generate A Recipe From Ingredients',
      generateDescription: 'Add ingredients below. We will first search the repository, then generate with AI only if needed.',
      generateInputPlaceholder: 'Type ingredient and press Enter…',
      generateAddIngredient: 'Add ingredient',
      generateRemoveIngredient: 'Remove ingredient',
      generateLoadingSuggestions: 'Loading suggestions…',
      generateHint: 'Add ingredients separated by commas (or press Enter after each ingredient). At least 3 ingredients are required.',
      generatePreviewTitle: 'Recipe Preview',
      generateSubmit: 'Generate Recipe',
      generateSubmitting: 'Generating…',
      generateMinIngredientsError: 'Please add at least 3 ingredients.',
      generateLoginRequired: 'Please sign in to generate recipes.',
      generateQuotaError: 'Daily generation limit reached (3/day).',
      generateUnexpectedError: 'Unable to generate recipe right now.',
      generateIngredientsTitle: 'Ingredients',
      generateStepsTitle: 'Preparation Steps',
      generateNutritionTitle: 'Nutrition Facts',
      generateSourceRepository: 'Found in Repository',
      generateSourceAi: 'AI Generated',
      generateSaveButton: 'Save to Repository',
      generateSaving: 'Saving…',
      generateSaveSuccess: 'Recipe saved successfully.',
      generateSaveError: 'Unable to save generated recipe.',
    },

    // Favorites Page
    favorites: {
      eyebrow: 'Saved Collection',
      title: 'My Favorites',
      accent: '& Saved',
      description: 'All the recipes you have saved.',
      empty: 'You have not saved any recipes yet.',
      emptyHint: 'Tap the heart on any recipe to save it here.',
      loginPrompt: 'Please sign in to see your favorites.',
      favoriteAdd: 'Save to favorites',
      favoriteRemove: 'Remove from favorites',
    },

    // Filter Sidebar
    filters: {
      originTitle: 'By Origin',
      styleTitle: 'By Style',
      editorNoteTitle: "Editor's Note",
      nutritionTitle: 'Nutrition Filter',
    },

    // Submit Page
    submit: {
      heroKicker: 'Drafting Room',
      heroTitle: 'The Organic Atelier',
      heroDescription:
        'Share your heritage and nourish the world. Document your culinary journey in our community of artisans.',
      editHeroKicker: 'Editor Console',
      editHeroTitle: 'Refine This Recipe',
      editHeroDescription:
        'Update the dish details, save your improvements, and keep the collection polished for every visitor.',
      cardSpanEdit: 'Editor Console',
      cardHeadEdit: 'Update Recipe',
      submitButtonCreate: 'Publish Recipe',
      submitButtonEdit: 'Update Recipe',
      cancelButton: 'Cancel',
      publishing: 'Publishing…',
      updating: 'Updating…',
      successCreate: 'Recipe published successfully.',
      successEdit: 'Recipe updated successfully.',
      errorCreate: 'Unable to publish recipe right now.',
      errorEdit: 'Unable to update this recipe right now.',
      validationError: 'Please add at least one ingredient and one instruction step before publishing.',
      uploadImage: 'Upload Recipe Image',
      imageTooLarge: 'Image is too large. Please choose a file under 5 MB.',
      titleLabel: 'Recipe Title',
      titlePlaceholder: 'e.g. Summer Fig and Prosciutto Crostini',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Fresh veggies, sun-kissed herbs, and cold-pressed oils...',
      prepTimeLabel: 'Prep Time (mins)',
      cookTimeLabel: 'Cook Time (mins)',
      servingsLabel: 'Servings',
      tagsLabel: 'Tags',
      tagsPlaceholder: 'salad, vegetarian',
      tagsHelp: 'Separate tags with commas.',
      ingredientsLabel: 'Ingredients List',
      ingredientNamePlaceholder: 'Ingredient name',
      nextIngredientPlaceholder: 'Next ingredient',
      quantityPlaceholder: 'Quantity',
      healthyAlternativesPlaceholder: 'Healthy alternatives (comma separated)',
      addIngredient: 'Add Another',
      modeFreeText: 'Free Text',
      modeSteps: 'Step-by-Step',
      instructionsLabel: 'Instructions',
      instructionsPlaceholder: 'Share your method... break it down step by step.',
      firstStepPlaceholder: 'Add first step...',
      nextStepPlaceholder: 'Add next step...',
      removeStep: 'Remove step',
      moveUp: 'Move up',
      moveDown: 'Move down',
      addStep: 'Add Step',
      loginRequired: 'You must be logged in to submit a recipe.',
    },

    // Recipe Detail
    recipeDetail: {
      loading: 'Loading this culinary story…',
      notFound: "We couldn't find this recipe.",
      snapshotTitle: 'Recipe Snapshot',
      prepTime: 'Prep Time',
      cookTime: 'Cook Time',
      totalTime: 'Total Time',
      servings: 'Servings',
      creator: 'Creator',
      creatorFallback: 'Community submission',
      ingredientsTitle: 'Ingredients',
      ingredientsFallback: 'Ingredients will appear once the recipe is finalized.',
      methodTitle: 'Method',
      methodFallback: 'Detailed steps will appear as soon as this chef shares their method.',
      healthyAlt: 'Healthy alternatives:',
      signatureRecipe: 'Signature Recipe',
      by: 'By',
      servingsLabel: 'servings',
    },

    // Articles
    articles: {
      eyebrow: 'Editorial',
      title: 'Articles',
      accent: '& Insights',
      description: 'Expert nutrition insights, wellness tips, and culinary deep-dives from our editors.',
      searchPlaceholder: 'Search articles…',
      searchScopeTitle: 'Title only',
      searchScopeContent: 'Full content',
      notFound: 'No articles found.',
      loading: 'Loading articles…',
      readMore: 'Read Article',
      backLabel: 'Back to Articles',
      shareLabel: 'Share',
      addArticle: '+ New Article',
      editArticle: 'Edit Article',
      deleteArticle: 'Delete Article',
      confirmDelete: 'Delete this article? This cannot be undone.',
      errorDelete: 'Unable to delete this article.',
      errorLoad: 'Unable to load articles right now.',
      editorTitle: 'New Article',
      editorTitleEdit: 'Edit Article',
      titleLabel: 'Title',
      titlePlaceholder: 'Article title…',
      summaryLabel: 'Summary',
      summaryPlaceholder: 'A short summary…',
      contentLabel: 'Content',
      contentPlaceholder: 'Write your article here…',
      authorLabel: 'Author',
      authorPlaceholder: 'Author name…',
      tagsLabel: 'Tags',
      tagsPlaceholder: 'nutrition, wellness',
      tagsHelp: 'Separate tags with commas.',
      uploadImage: 'Upload Cover Image',
      imageTooLarge: 'Image is too large. Please choose a file under 5 MB.',
      publishButton: 'Publish Article',
      updateButton: 'Update Article',
      publishingButton: 'Publishing…',
      updatingButton: 'Updating…',
      cancelButton: 'Cancel',
      successCreate: 'Article published.',
      successEdit: 'Article updated.',
      errorCreate: 'Unable to publish article.',
      errorEdit: 'Unable to update article.',
      by: 'By',
      noArticles: 'No articles yet. Check back soon.',
    },

    // Auth
    auth: {
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to your account.',
      loginCardTitle: 'Sign in to continue',
      loginCardSubtitle: 'Use your email or username and password to sign in.',
      registerTitle: 'Create Account',
      registerCardTitle: 'Create your account',
      registerCardSubtitle: 'Register with your first name, last name, email, and password.',
      emailOrUsername: 'Email or Username',
      emailOrUsernamePlaceholder: 'your email or username',
      firstName: 'First Name',
      firstNamePlaceholder: 'First name',
      lastName: 'Last Name',
      lastNamePlaceholder: 'Last name',
      username: 'Username',
      usernamePlaceholder: 'Choose a username',
      emailAddress: 'Email Address',
      emailPlaceholder: 'yourname@example.com',
      password: 'Password',
      forgotPassword: 'Help',
      submittingLogin: 'Signing In…',
      submittingRegister: 'Creating Account…',
      submitLogin: 'Sign In',
      submitRegister: 'Create Account',
      switchToRegister: 'Register Now',
      switchToLogin: 'Sign In',
      noAccount: "Don't have an account?",
      haveAccount: 'Already have an account?',
    },

    // Food Diary
    diary: {
      navLabel: 'Food Diary',
      pageTitle: 'My Food Diary',
      tabLog: 'Log',
      tabInsights: 'Insights',
      addEntry: 'Add Food',
      editEntry: 'Edit',
      deleteEntry: 'Delete',
      entryFoodLabel: 'Food / Dish',
      entryFoodPlaceholder: 'e.g. egg, oatmeal with banana…',
      entryQuantityLabel: 'Quantity',
      entryQuantityPlaceholder: 'e.g. 150, 2, 0.5',
      entryUnitLabel: 'Unit',
      // DiaryEntryForm keys
      foodNameLabel: 'Food / Dish',
      foodNamePlaceholder: 'e.g. egg, oatmeal with banana…',
      quantityLabel: 'Qty',
      unitLabel: 'Unit',
      unit_g: 'g (gram)',
      unit_kg: 'kg (kilogram)',
      unit_ml: 'ml (milliliter)',
      unit_l: 'l (liter)',
      unit_cup: 'cup',
      unit_tbsp: 'tbsp (tablespoon)',
      unit_tsp: 'tsp (teaspoon)',
      unit_slice: 'slice',
      unit_piece: 'piece',
      unitOther: 'Other…',
      unitCustomPlaceholder: 'e.g. bowl',
      addButton: 'Add',
      unitOptions: {
        g: 'g (gram)',
        kg: 'kg (kilogram)',
        ml: 'ml (milliliter)',
        l: 'l (liter)',
        oz: 'oz (ounce)',
        lb: 'lb (pound)',
        cup: 'cup',
        tbsp: 'tbsp (tablespoon)',
        tsp: 'tsp (teaspoon)',
        slice: 'slice',
        piece: 'piece',
        serving: 'serving',
        other: 'Other…',
      },
      saveEntry: 'Save',
      cancelEntry: 'Cancel',
      calculateNutrition: 'Calculate Nutrition',
      nutritionLimitReached: 'Daily calculation limit reached (5/day)',
      nutritionNotBasic: 'Upgrade to BASIC to unlock nutrition calculation',
      emptyDay: 'Nothing logged for this day yet.',
      todayLabel: 'Today',
      prevWeek: 'Previous week',
      nextWeek: 'Next week',
      viewDaily: 'Daily',
      viewWeekly: 'Weekly',
      viewMonthly: 'Monthly',
      viewCustom: 'Custom',
      noInsightsData: 'No nutrition data for the selected period. Calculate nutrition for diary days to see your insights.',
      insightsDisclaimer: 'These are general estimates only and not medical advice.',
      personalizedTargets: 'Personalized targets',
      genericTargets: 'General recommendations (WHO/FDA)',
      staleNutritionWarning: "You've edited entries after calculating — recalculate to update your results.",
    },

    // Dietary Preferences
    preferences: {
      navLabel: 'My Preferences',
      pageTitle: 'Dietary Preferences',
      savedBanner: 'Preferences saved successfully.',
      genderLabel: 'Gender',
      genderMale: 'Male',
      genderFemale: 'Female',
      genderOther: 'Other',
      genderPreferNot: 'Prefer not to say',
      ageLabel: 'Age',
      heightLabel: 'Height',
      weightLabel: 'Weight',
      restrictionsLabel: 'Dietary Restrictions',
      dietLabel: 'Diet Preferences',
      healthLabel: 'Health Conditions',
      healthNone: 'None',
      healthHighBP: 'High Blood Pressure',
      healthDiabetes: 'Diabetes',
      healthCholesterol: 'High Cholesterol',
      healthOther: 'Other',
      healthOtherLabel: 'Please describe',
      skip: 'Skip',
      back: 'Back',
      next: 'Next',
      save: 'Save',
      exitAndSave: 'Exit & Save',
      savingStep: 'Saving…',
      savingFinal: 'Saving…',
      stepOf: 'Step {step} of {total}',
      stepTitle1: 'Personal Stats',
      stepTitle2: 'Dietary Restrictions',
      stepTitle3: 'Diet Preferences',
      stepTitle4: 'Medical Conditions',
      stepSubtitle1: 'Gender, height and weight',
      stepSubtitle2: 'Select dietary restrictions',
      stepSubtitle3: 'Choose your preferred eating style',
      stepSubtitle4: 'Tell us about relevant conditions',
      restrictionHint: 'Used to tailor safe recipe suggestions',
      dietHint: 'Influences macro balance and style',
      restrictionGlutenFree: 'Gluten-Free',
      restrictionDairyFree: 'Dairy-Free',
      restrictionNutFree: 'Nut-Free',
      restrictionVegan: 'Vegan',
      restrictionVegetarian: 'Vegetarian',
      restrictionHalal: 'Halal',
      restrictionKosher: 'Kosher',
      restrictionLowSodium: 'Low-Sodium',
      restrictionPeanutFree: 'Peanut-Free',
      dietKeto: 'Keto',
      dietLowCarb: 'Low-Carb',
      dietHighProtein: 'High-Protein',
      dietMediterranean: 'Mediterranean',
      dietPaleo: 'Paleo',
      dietSugarFree: 'Sugar-Free',
      dietPlantBased: 'Plant-Based',
      dietBalanced: 'Balanced',
    },

    // Nutrition Panel
    nutrition: {
      panelTitle: 'Nutrition Facts',
      perServing: 'per serving',
      calories: 'Calories',
      fat: 'Total Fat',
      protein: 'Protein',
      carbs: 'Carbohydrates',
      sugar: 'Sugar',
      fiber: 'Dietary Fiber',
      iron: 'Iron',
      vitamins: 'Vitamins',
      otherNutrients: 'Other Nutrients',
      analyzed: 'AI-analyzed estimates',
      notAvailable: 'Nutritional data is being calculated…',

      tags: {
        gluten_free: 'Gluten-Free',
        high_calorie: 'High Calorie',
        high_protein: 'High Protein',
        high_carbs: 'High Carbs',
        high_sugar: 'High Sugar',
        dietary_fiber: 'High Fiber',
        high_fat: 'High Fat',
        low_sugar: 'Low Sugar',
        low_sodium: 'Low Sodium',
        low_fat: 'Low Fat',
        low_carb: 'Low Carb',
      },
    },
  },

  he: {
    // NavBar
    nav: {
      brand: 'WellnessCraft',
      home: 'בית',
      explorer: 'מתכונים',
      submit: 'הוסף מתכון',
      favorites: 'מועדפים',
      tips: 'טיפים',
      search: 'חיפוש מתכונים...',
      notifications: 'התראות',
      account: 'חשבון',
      logout: 'התנתק',
      switchLang: 'En',
      articles: 'מאמרים',
    },

    // Hero
    hero: {
      headingLead: 'מתכוני גורמה',
      headingTrail: 'לבריאות ולאורח החיים שלך',
      highlight: 'מותאמים',
      description:
        'קח את מסע הבריאות שלך לשלב הבא עם הנחיה תזונתית מקצועית. גלה תחליפים בריאים לרכיבים ואוסף מתכונים מיוחד – ללא סוכר, מתאים להריון, עשיר בחלבון ועוד.',
      primaryAction: { label: 'התחל את המסע', href: '/heb/explorer' },
      generateAction: 'צור מתכון לפי רכיבים',
      weeklyPickLabel: 'בחירת השבוע',
      weeklyPick: 'החלפה בריאה • מחליפים סוכר מעובד בנקטר תמרים',
      imageAlt: 'קערת סלט צבעונית עם תוצרת עונתית',
    },

    // Gourmet Section
    gourmet: {
      heading: 'בחירות הגורמה של היום',
      description:
        'מנות המיועדות למטרות בריאות ספציפיות. גלו צלחות עשירות בחלבון, ללא סוכר, או למאזן משקל.',
      prevLabel: 'בחירה קודמת',
      nextLabel: 'בחירה הבאה',
      saveLabel: 'שמור מתכון',
    },

    // Newsletter / Consultation
    newsletter: {
      heading: 'הזמן ייעוץ ראשוני אישי 1:1',
      body2:
        'אני מזמינה אותך לפנות לייעוץ ראשוני כדי שנוכל להתחבר, לדון בצרכים הספציפיים שלך ולבחון כיצד תזונה קלינית, כלי אימון ואסטרטגיות פסיכולוגיות מבוססות ראיות יכולים לתמוך במסעך.',
      placeholder: 'שם@דוגמה.com',
      emailLabel: 'כתובת האימייל שלך',
      sendButton: 'שלח בקשה',
      sendingButton: 'שולח…',
      defaultMeta:
        '',
    },

    // Explorer Page
    explorer: {
      eyebrow: 'אוסף נבחר',
      title: 'מתכונים',
      accent: 'וארכיון',
      description:
        'אוסף נבחר של מתכוני בריאות מותאמים לדרך חיים.',
      filterOpen: 'פתח מסננים',
      recipeMetaFallback: 'תזונה מערכתית',
      addRecipe: 'הוסף מתכון חדש',
      generateEntryPoint: 'צור מתכון לפי רכיבים',
      searchPlaceholder: 'חיפוש מתכונים…',
      searchScopeTitle: 'כותרת בלבד',
      searchScopeContent: 'תוכן מלא',
      searchNoResults: 'לא נמצאו מתכונים התואמים לחיפוש.',
      modeSearch: 'חיפוש קיים',
      modeGenerate: 'יצירה לפי רכיבים',
      generateTitle: 'יצירת מתכון לפי רשימת רכיבים',
      generateDescription: 'הוסף רכיבים. קודם נחפש במאגר, ורק אם אין התאמה ניצור מתכון בעזרת AI.',
      generateInputPlaceholder: 'הקלד רכיב ולחץ Enter…',
      generateAddIngredient: 'הוסף רכיב',
      generateRemoveIngredient: 'הסר רכיב',
      generateLoadingSuggestions: 'טוען הצעות…',
      generateHint: 'הוסף רכיבים עם פסיק בין רכיב לרכיב (או לחץ Enter אחרי כל רכיב). נדרשים לפחות 3 רכיבים.',
      generatePreviewTitle: 'תצוגה מקדימה של המתכון',
      generateSubmit: 'צור מתכון',
      generateSubmitting: 'יוצר…',
      generateMinIngredientsError: 'יש להוסיף לפחות 3 רכיבים.',
      generateLoginRequired: 'יש להתחבר כדי ליצור מתכונים.',
      generateQuotaError: 'הגעת למגבלה היומית (3 יצירות ביום).',
      generateUnexpectedError: 'לא ניתן ליצור מתכון כעת.',
      generateIngredientsTitle: 'רכיבים',
      generateStepsTitle: 'שלבי הכנה',
      generateNutritionTitle: 'ערכים תזונתיים',
      generateSourceRepository: 'נמצא במאגר',
      generateSourceAi: 'נוצר ב-AI',
      generateSaveButton: 'שמור למאגר',
      generateSaving: 'שומר…',
      generateSaveSuccess: 'המתכון נשמר בהצלחה.',
      generateSaveError: 'לא ניתן לשמור את המתכון שנוצר.',
    },

    // Favorites Page
    favorites: {
      eyebrow: 'אוסף שמור',
      title: 'המועדפים שלי',
      accent: 'ושמורים',
      description: 'כל המתכונים שסימנת לשמירה.',
      empty: 'עדיין לא שמרת מתכונים.',
      emptyHint: 'לחץ על הלב בכל מתכון כדי לשמור אותו כאן.',
      loginPrompt: 'יש להתחבר כדי לראות את המועדפים שלך.',
      favoriteAdd: 'שמור במועדפים',
      favoriteRemove: 'הסר ממועדפים',
    },

    // Filter Sidebar
    filters: {
      originTitle: 'לפי מוצא',
      styleTitle: 'לפי סגנון',
      editorNoteTitle: 'הערת העורך',
      nutritionTitle: 'סינון תזונתי',
    },

    // Submit Page
    submit: {
      heroKicker: 'חדר הטיוטות',
      heroTitle: 'שתף מתכון',
      heroDescription:
        ' תעד את מסע הבישול שלך.',
      editHeroKicker: 'קונסולת העריכה',
      editHeroTitle: 'עדכן מתכון זה',
      editHeroDescription:
        'עדכן את פרטי המנה, שמור את השיפורים שלך ושמור על האוסף מלוטש לכל מבקר.',
      cardSpanEdit: 'קונסולת עריכה',
      cardHeadEdit: 'עדכן מתכון',
      submitButtonCreate: 'פרסם מתכון',
      submitButtonEdit: 'עדכן מתכון',
      cancelButton: 'ביטול',
      publishing: 'מפרסם…',
      updating: 'מעדכן…',
      successCreate: 'המתכון פורסם בהצלחה.',
      successEdit: 'המתכון עודכן בהצלחה.',
      errorCreate: 'לא ניתן לפרסם את המתכון כעת.',
      errorEdit: 'לא ניתן לעדכן את המתכון כעת.',
      validationError: 'יש להוסיף לפחות רכיב אחד ושלב הכנה אחד לפני הפרסום.',
      uploadImage: 'העלה תמונת מתכון',
      imageTooLarge: 'התמונה גדולה מדי. אנא בחר קובץ שקטן משל 5 MB.',
      titleLabel: 'כותרת המתכון',
      titlePlaceholder: 'למשל: קרוסטיני תאנים ופרושוטו קיצי',
      descriptionLabel: 'תיאור',
      descriptionPlaceholder: 'ירקות טריים, עשבי תיבול רעננים ושמנים בכבישה קרה...',
      prepTimeLabel: 'זמן הכנה (דקות)',
      cookTimeLabel: 'זמן בישול (דקות)',
      servingsLabel: 'מנות',
      tagsLabel: 'תגיות',
      tagsPlaceholder: 'סלט, צמחוני',
      tagsHelp: 'הפרד תגיות באמצעות פסיקים.',
      ingredientsLabel: 'רשימת רכיבים',
      ingredientNamePlaceholder: 'שם הרכיב',
      nextIngredientPlaceholder: 'הרכיב הבא',
      quantityPlaceholder: 'כמות',
      healthyAlternativesPlaceholder: 'חלופות בריאות (מופרדות בפסיקים)',
      addIngredient: 'הוסף רכיב נוסף',
      modeFreeText: 'טקסט חופשי',
      modeSteps: 'שלב אחר שלב',
      instructionsLabel: 'הוראות הכנה',
      instructionsPlaceholder: 'שתף את שיטת ההכנה... פרט אותה שלב אחר שלב.',
      firstStepPlaceholder: 'הוסף את השלב הראשון...',
      nextStepPlaceholder: 'הוסף את השלב הבא...',
      removeStep: 'הסר שלב',
      moveUp: 'הזז למעלה',
      moveDown: 'הזז למטה',
      addStep: 'הוסף שלב',
      loginRequired: 'עליך להתחבר כדי להוסיף מתכון.',
    },

    // Recipe Detail
    recipeDetail: {
      backLabel: 'חזרה למתכונים',
      loading: 'טוען את הסיפור הקולינרי הזה…',
      notFound: 'לא מצאנו את המתכון הזה.',
      snapshotTitle: 'סיכום המתכון',
      prepTime: 'זמן הכנה',
      cookTime: 'זמן בישול',
      totalTime: 'זמן כולל',
      servings: 'מנות',
      creator: 'יוצר המתכון',
      creatorFallback: 'הגשה מהקהילה',
      ingredientsTitle: 'מצרכים',
      ingredientsFallback: 'המצרכים יופיעו לאחר השלמת המתכון.',
      methodTitle: 'אופן הכנה',
      methodFallback: 'השלבים המפורטים יופיעו ברגע שהשף ישתף את השיטה.',
      healthyAlt: 'תחליפים בריאים:',
      signatureRecipe: 'מתכון ייחודי',
      by: 'מאת',
      servingsLabel: 'מנות',
    },

    // Articles
    articles: {
      eyebrow: 'מאמרים',
      title: 'מאמרים',
      accent: 'ותובנות',
      description: 'תובנות תזונה מקצועיות, טיפי בריאות ועומק קולינרי מהעורכים שלנו.',
      searchPlaceholder: 'חיפוש מאמרים…',
      searchScopeTitle: 'כותרת בלבד',
      searchScopeContent: 'תוכן מלא',
      notFound: 'לא נמצאו מאמרים.',
      loading: 'טוען מאמרים…',
      readMore: 'קרא מאמר',
      backLabel: 'חזרה למאמרים',
      shareLabel: 'שיתוף',
      addArticle: '+ מאמר חדש',
      editArticle: 'ערוך מאמר',
      deleteArticle: 'מחק מאמר',
      confirmDelete: 'למחוק את המאמר? פעולה זו לא ניתנת לביטול.',
      errorDelete: 'לא ניתן למחוק את המאמר.',
      errorLoad: 'לא ניתן לטעון מאמרים כרגע.',
      editorTitle: 'מאמר חדש',
      editorTitleEdit: 'ערוך מאמר',
      titleLabel: 'כותרת',
      titlePlaceholder: 'כותרת המאמר…',
      summaryLabel: 'תקציר',
      summaryPlaceholder: 'תקציר קצר…',
      contentLabel: 'תוכן',
      contentPlaceholder: 'כתוב את המאמר כאן…',
      authorLabel: 'מחבר',
      authorPlaceholder: 'שם המחבר…',
      tagsLabel: 'תגיות',
      tagsPlaceholder: 'תזונה, בריאות',
      tagsHelp: 'הפרד תגיות בפסיקים.',
      uploadImage: 'העלה תמונת שער',
      imageTooLarge: 'התמונה גדולה מדי. בחר קובץ מתחת ל-5 MB.',
      publishButton: 'פרסם מאמר',
      updateButton: 'עדכן מאמר',
      publishingButton: 'מפרסם…',
      updatingButton: 'מעדכן…',
      cancelButton: 'ביטול',
      successCreate: 'המאמר פורסם.',
      successEdit: 'המאמר עודכן.',
      errorCreate: 'לא ניתן לפרסם את המאמר.',
      errorEdit: 'לא ניתן לעדכן את המאמר.',
      by: 'מאת',
      noArticles: 'אין כתבות עדיין. בקר שוב בקרוב.',
    },

    // Auth
    auth: {
      loginTitle: 'ברוך שובך',
      loginSubtitle: 'התחבר לחשבונך.',
      loginCardTitle: 'התחבר להמשך',
      loginCardSubtitle: 'השתמש באימייל או שם המשתמש שלך כדי להתחבר.',
      registerTitle: 'צור חשבון',
      registerCardTitle: 'צור את החשבון שלך',
      registerCardSubtitle: 'הירשם עם שם פרטי, שם משפחה, אימייל וסיסמה.',
      emailOrUsername: 'אימייל או שם משתמש',
      emailOrUsernamePlaceholder: 'האימייל או שם המשתמש שלך',
      firstName: 'שם פרטי',
      firstNamePlaceholder: 'שם פרטי',
      lastName: 'שם משפחה',
      lastNamePlaceholder: 'שם משפחה',
      username: 'שם משתמש',
      usernamePlaceholder: 'בחר שם משתמש',
      emailAddress: 'כתובת אימייל',
      emailPlaceholder: 'yourname@example.com',
      password: 'סיסמה',
      forgotPassword: 'עזרה',
      submittingLogin: 'מתחבר…',
      submittingRegister: 'יוצר חשבון…',
      submitLogin: 'התחבר',
      submitRegister: 'צור חשבון',
      switchToRegister: 'הירשם עכשיו',
      switchToLogin: 'התחבר',
      noAccount: 'אין לך חשבון?',
      haveAccount: 'כבר יש לך חשבון?',
    },

    // Food Diary
    diary: {
      navLabel: 'יומן אכילה',
      pageTitle: 'יומן האכילה שלי',
      tabLog: 'תיעוד',
      tabInsights: 'תובנות',
      addEntry: 'הוסף מזון',
      editEntry: 'עריכה',
      deleteEntry: 'מחיקה',
      entryFoodLabel: 'מזון / מנה',
      entryFoodPlaceholder: 'למשל: ביצה, שיבולת שועל עם בננה…',
      entryQuantityLabel: 'כמות',
      entryQuantityPlaceholder: 'למשל: 150, 2, 0.5',
      entryUnitLabel: 'יחידת מידה',
      // DiaryEntryForm keys (Hebrew translations)
      foodNameLabel: 'מזון / מנה',
      foodNamePlaceholder: 'למשל: ביצה, שיבולת שועל עם בננה…',
      quantityLabel: 'כמות',
      unitLabel: 'יחידת מידה',
      unit_g: 'גרם',
      unit_kg: 'קילוגרם',
      unit_ml: 'מיליליטר',
      unit_l: 'ליטר',
      unit_cup: 'כוס',
      unit_tbsp: 'כף',
      unit_tsp: 'כפית',
      unit_slice: 'פרוסה',
      unit_piece: 'יחידה',
      unitOther: 'אחר…',
      unitCustomPlaceholder: 'למשל: קערה',
      addButton: 'הוסף',
      unitOptions: {
        g: 'גרם',
        kg: 'קילוגרם',
        ml: 'מיליליטר',
        l: 'ליטר',
        oz: 'אאונס',
        lb: 'פאונד',
        cup: 'כוס',
        tbsp: 'כף',
        tsp: 'כפית',
        slice: 'פרוסה',
        piece: 'יחידה',
        serving: 'מנה',
        other: 'אחר…',
      },
      saveEntry: 'שמור',
      cancelEntry: 'ביטול',
      calculateNutrition: 'חשב ערכים תזונתיים',
      nutritionLimitReached: 'הגעת למגבלה היומית (5 חישובים ליום)',
      nutritionNotBasic: 'שדרג לחשבון BASIC כדי לחשב ערכים תזונתיים',
      emptyDay: 'לא תועד עדיין מזון ביום זה.',
      todayLabel: 'היום',
      prevWeek: 'שבוע קודם',
      nextWeek: 'שבוע הבא',
      viewDaily: 'יומי',
      viewWeekly: 'שבועי',
      viewMonthly: 'חודשי',
      viewCustom: 'טווח מותאם',
      noInsightsData: 'אין נתוני תזונה לתקופה הנבחרת. חשב ערכים תזונתיים עבור ימים ביומן כדי לראות את התובנות שלך.',
      insightsDisclaimer: 'אלו הערכות כלליות בלבד ואינן מהוות ייעוץ רפואי.',
      personalizedTargets: 'יעדים מותאמים אישית',
      genericTargets: 'המלצות כלליות (WHO/FDA)',
      staleNutritionWarning: 'ערכות את הרשומות לאחר החישוב — חשב מחדש לעדכון התוצאות.',
    },

    // Dietary Preferences
    preferences: {
      navLabel: 'ההעדפות שלי',
      pageTitle: 'העדפות תזונתיות',
      savedBanner: 'ההעדפות נשמרו בהצלחה.',
      genderLabel: 'מין',
      genderMale: 'זכר',
      genderFemale: 'נקבה',
      genderOther: 'אחר',
      genderPreferNot: 'מעדיף לא לציין',
      ageLabel: 'גיל',
      heightLabel: 'גובה',
      weightLabel: 'משקל',
      restrictionsLabel: 'הגבלות תזונתיות',
      dietLabel: 'העדפות דיאטה',
      healthLabel: 'מצבים רפואיים',
      healthNone: 'ללא',
      healthHighBP: 'לחץ דם גבוה',
      healthDiabetes: 'סוכרת',
      healthCholesterol: 'כולסטרול גבוה',
      healthOther: 'אחר',
      healthOtherLabel: 'נא לתאר',
      skip: 'דלג',
      back: 'חזרה',
      next: 'הבא',
      save: 'שמור',
      exitAndSave: 'יציאה ושמירה',
      savingStep: 'שומר…',
      savingFinal: 'שומר…',
      stepOf: 'שלב {step} מתוך {total}',
      stepTitle1: 'נתונים אישיים',
      stepTitle2: 'הגבלות תזונתיות',
      stepTitle3: 'העדפות דיאטה',
      stepTitle4: 'מצבים רפואיים',
      stepSubtitle1: 'מין, גובה ומשקל',
      stepSubtitle2: 'בחר הגבלות תזונתיות',
      stepSubtitle3: 'בחר סגנון תזונה מועדף',
      stepSubtitle4: 'ספר לנו על מצבים רלוונטיים',
      restrictionHint: 'עוזר להתאים מתכונים בטוחים עבורך',
      dietHint: 'משפיע על איזון הרכיבים התזונתיים',
      restrictionGlutenFree: 'ללא גלוטן',
      restrictionDairyFree: 'ללא חלב',
      restrictionNutFree: 'ללא אגוזים',
      restrictionVegan: 'טבעוני',
      restrictionVegetarian: 'צמחוני',
      restrictionHalal: 'חלאל',
      restrictionKosher: 'כשר',
      restrictionLowSodium: 'דל נתרן',
      restrictionPeanutFree: 'ללא בוטנים',
      dietKeto: 'קטו',
      dietLowCarb: 'דל פחמימות',
      dietHighProtein: 'עתיר חלבון',
      dietMediterranean: 'ים תיכוני',
      dietPaleo: 'פליאו',
      dietSugarFree: 'ללא סוכר',
      dietPlantBased: 'מבוסס צומח',
      dietBalanced: 'מאוזן',
    },

    // Nutrition Panel
    nutrition: {
      panelTitle: 'ערכים תזונתיים',
      perServing: 'למנה',
      calories: 'קלוריות',
      fat: 'שומן כולל',
      protein: 'חלבון',
      carbs: 'פחמימות',
      sugar: 'סוכר',
      fiber: 'סיבים תזונתיים',
      iron: 'ברזל',
      vitamins: 'ויטמינים',
      otherNutrients: 'נוטריינטים נוספים',
      analyzed: 'הערכות בניתוח AI',
      notAvailable: 'נתוני תזונה בחישוב…',
      tags: {
        gluten_free: 'ללא גלוטן',
        high_calorie: 'עתיר קלוריות',
        high_protein: 'עתיר חלבון',
        high_carbs: 'עתיר פחמימות',
        high_sugar: 'עתיר סוכר',
        dietary_fiber: 'עשיר בסיבים',
        high_fat: 'עתיר שומן',
        low_sugar: 'דל סוכר',
        low_sodium: 'דל נתרן',
        low_fat: 'דל שומן',
        low_carb: 'דל פחמימות',
      },
    },
  },
};

export default translations;
